import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import VLFSelectNew from "@/components/Input/SelectNew";
import VLFCard from "@/components/Card";
import DataTable from "@/components/DataTable";
import EmptyData from "@/components/EmptyData";
import Kpi, {KpiRow} from "@/components/Kpi";
import MainActions from "@/components/MainActions";
import StatusBadge from "@/components/StatusBadge";

import HandleFeeModal from "@/pages/Secretariat/HandleFeeModal";
import HandleInstallmentModal from "@/pages/Secretariat/HandleInstallmentModal";

import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";

import {FeeAPI} from "@/api/fee";
import {PlayerAPI} from "@/api/player";
import {RosterAPI} from "@/api/roster";
import {visibleRosters} from "@/utils/functions";
import {
	CERTIFICATE_STATUS,
	certificateNote,
	certificateStatus,
} from "@/utils/certificates";

import {
	ERROR_OPERATION,
	getInternalClubId,
	SUCCESSFUL_OPERATION,
} from "@/utils/constants";

import "./index.scss";

// ── Helpers ───────────────────────────────────────────────────────────────────

const feeStatus = (fee) => {
	if (!fee?.installments?.length) return "unpaid";
	const paid = fee.installments.filter((i) => i.status === "paid");
	if (paid.length === fee.installments.length) return "paid";
	const hasOverdue = fee.installments.some(
		(i) => i.status !== "paid" && moment(i.dueDate).isBefore(moment()),
	);
	if (hasOverdue) return "overdue";
	if (paid.length > 0) return "partial";
	return "unpaid";
};

const feeStatusLabel = {
	paid: "Pagata",
	partial: "Parziale",
	unpaid: "Non pagata",
	overdue: "Scaduta",
};

const feeStatusTone = {
	paid: "good",
	partial: "warning",
	unpaid: "neutral",
	overdue: "critical",
};

const paidAmount = (fee) => {
	if (!fee?.installments?.length) return 0;
	return fee.installments
		.filter((i) => i.status === "paid")
		.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
};

// ── Component ─────────────────────────────────────────────────────────────────

function SecretariatPage(props) {
	const {handleNotification} = props;

	const navigate = useNavigate();
	const seasonId = useSelector((state) => state?.season?.id);

	const [loading, setLoading] = useState(false);
	const [rosters, setRosters] = useState([]);
	const [selectedRoster, setSelectedRoster] = useState(null);
	const [players, setPlayers] = useState([]);
	const [fees, setFees] = useState([]);
	const [certificates, setCertificates] = useState({});

	const [openFeeModal, setOpenFeeModal] = useState(false);
	const [openInstallmentModal, setOpenInstallmentModal] = useState(false);
	// false quando il modulo quote non risponde (non ancora attivo sul server)
	const [feesAvailable, setFeesAvailable] = useState(true);
	const [selectedInstallment, setSelectedInstallment] = useState(null);

	// ── Load rosters ──────────────────────────────────────────────────────────

	useEffect(() => {
		if (!seasonId) return;
		RosterAPI.list({seasonId, clubId: getInternalClubId()})
			.then((res) => {
				const internal = visibleRosters(
					(res ?? []).filter(
						(r) =>
							!r.prospect &&
							!r.playerCategory?.basicActivities &&
							(!r.clubId || r.clubId === getInternalClubId()),
					),
				);
				setRosters(internal);
				if (internal.length) {
					setSelectedRoster({
						value: internal[0].id,
						label:
							internal[0].name || internal[0].playerCategory?.name || "Rosa",
					});
				}
			})
			.catch(() => handleNotification(ERROR_OPERATION, "error"));
	}, [seasonId]); // eslint-disable-line react-hooks/exhaustive-deps

	// ── Load data when roster changes ─────────────────────────────────────────

	useEffect(() => {
		if (!selectedRoster || !seasonId) return;
		loadData(selectedRoster.value);
	}, [selectedRoster?.value, seasonId]); // eslint-disable-line react-hooks/exhaustive-deps

	const loadData = async (rosterId) => {
		setLoading(true);
		try {
			const [playersRes, feesRes] = await Promise.all([
				RosterAPI.getPlayers(rosterId),
				FeeAPI.list({seasonId, rosterId}).catch(() => null),
			]);

			const internalPlayers = (playersRes || [])
				.map((rp) => rp.player)
				.filter((p) => p && p.status === "Interno");

			setPlayers(internalPlayers);
			setFeesAvailable(feesRes !== null);
			setFees(feesRes || []);

			// Carica certificati per ogni giocatore
			const certsMap = {};
			await Promise.all(
				internalPlayers.map(async (p) => {
					try {
						const certs = await PlayerAPI.getCertificates(p.id);
						certsMap[p.id] = certs || [];
					} catch {
						certsMap[p.id] = [];
					}
				}),
			);
			setCertificates(certsMap);
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	// ── Handlers ──────────────────────────────────────────────────────────────

	const handleCreateFee = async (data) => {
		try {
			setLoading(true);
			await FeeAPI.create({...data, seasonId, rosterId: selectedRoster?.value});
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenFeeModal(false);
			loadData(selectedRoster?.value);
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const handlePayInstallment = async (data) => {
		try {
			setLoading(true);
			await FeeAPI.updateInstallment(
				selectedInstallment.feeId,
				selectedInstallment.id,
				{
					...selectedInstallment,
					...data,
					status: "paid",
				},
			);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setOpenInstallmentModal(false);
			setSelectedInstallment(null);
			loadData(selectedRoster?.value);
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	// ── Derived data ──────────────────────────────────────────────────────────

	const playerFeeMap = {};
	(fees || []).forEach((f) => {
		if (f.playerId) playerFeeMap[f.playerId] = f;
	});

	const playersWithStatus = players.map((p) => {
		const fee = playerFeeMap[p.id];
		const certs = certificates[p.id] || [];
		return {
			player: p,
			fee,
			status: fee ? feeStatus(fee) : "unpaid",
			paidAmount: fee ? paidAmount(fee) : 0,
			totalAmount: fee ? parseFloat(fee.totalAmount) || 0 : 0,
			overdueCount: fee
				? (fee.installments || []).filter(
						(i) => i.status !== "paid" && moment(i.dueDate).isBefore(moment()),
					).length
				: 0,
			certificate: certificateStatus(certs),
		};
	});

	// KPI
	const totalExpected = playersWithStatus.reduce(
		(s, p) => s + p.totalAmount,
		0,
	);
	const totalCollected = playersWithStatus.reduce(
		(s, p) => s + p.paidAmount,
		0,
	);
	const paidCount = playersWithStatus.filter((p) => p.status === "paid").length;
	const overdueCount = playersWithStatus.filter(
		(p) => p.status === "overdue",
	).length;
	const expiredDocsCount = playersWithStatus.filter(
		(p) => p.certificate.status === "expired",
	).length;
	const expiringDocsCount = playersWithStatus.filter(
		(p) => p.certificate.status === "expiring",
	).length;

	const rosterOptions = rosters.map((r) => ({
		value: r.id,
		label: r.name || r.playerCategory?.name || "Rosa",
	}));

	const incassoPerc =
		totalExpected > 0 ? Math.round((totalCollected / totalExpected) * 100) : 0;
	const euro = (n) => n.toLocaleString("it-IT") + " €";

	const payNext = (fee) => {
		const nextUnpaid = (fee.installments || [])
			.filter((i) => i.status !== "paid")
			.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))[0];
		if (nextUnpaid) {
			setSelectedInstallment({...nextUnpaid, feeId: fee.id});
			setOpenInstallmentModal(true);
		}
	};

	const columns = [
		{
			key: "name",
			label: "Giocatore",
			render: (r) => (
				<>
					<div className="sec__name">
						{r.player.firstName} {r.player.lastName}
					</div>
					<div className="sec__role">{r.player.primaryRole || ""}</div>
				</>
			),
			sortValue: (r) => r.player.lastName,
		},
		...(feesAvailable
			? [
					{
						key: "amount",
						label: "Quota",
						render: (r) =>
							r.totalAmount > 0
								? euro(r.paidAmount) + " su " + euro(r.totalAmount)
								: "—",
						sortValue: (r) => r.totalAmount - r.paidAmount,
					},
					{
						key: "status",
						label: "Stato",
						render: (r) => (
							<StatusBadge
								tone={feeStatusTone[r.status]}
								label={
									feeStatusLabel[r.status] +
									(r.overdueCount ? ` (${r.overdueCount} rate scadute)` : "")
								}
							/>
						),
						sortValue: (r) => r.status,
					},
				]
			: []),
		{
			key: "docs",
			label: "Certificato medico",
			render: (r) => (
				<StatusBadge
					tone={CERTIFICATE_STATUS[r.certificate.status].tone}
					label={
						r.certificate.status === "ok"
							? CERTIFICATE_STATUS.ok.label
							: certificateNote(r.certificate)
					}
				/>
			),
			sortValue: (r) => r.certificate.daysLeft,
		},
		...(feesAvailable
			? [
					{
						key: "action",
						label: "",
						align: "end",
						render: (r) =>
							r.fee &&
							(r.fee.installments || []).some((i) => i.status !== "paid") ? (
								<div onClick={(e) => e.stopPropagation()}>
									<MainActions
										actions={[
											{
												icon: <CheckIcon />,
												label: "Segna pagata",
												variant: "outlined",
												onClick: () => payNext(r.fee),
											},
										]}
									/>
								</div>
							) : null,
					},
				]
			: []),
	];

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				current="Segreteria"
				actions={
					feesAvailable && selectedRoster ? (
						<MainActions
							actions={[
								{
									icon: <AddIcon />,
									title: "Nuova quota",
									onClick: () => setOpenFeeModal(true),
								},
							]}
						/>
					) : null
				}
			/>

			<div className="sec">
				<div className="sec__roster">
					<VLFSelectNew
						label="Squadra"
						options={rosterOptions}
						sorted={false}
						isClearable={false}
						value={selectedRoster}
						onChange={setSelectedRoster}
					/>
				</div>

				{selectedRoster && (
					<>
						<KpiRow>
							<Kpi label="Giocatori" value={players.length} />
							{feesAvailable && (
								<>
									<Kpi
										label="Quote pagate"
										value={`${paidCount}/${playersWithStatus.length}`}
									/>
									<Kpi
										label="Incassato"
										value={incassoPerc + "%"}
										tone={
											totalExpected
												? incassoPerc < 50
													? "critical"
													: "good"
												: undefined
										}
										note={`${euro(totalCollected)} su ${euro(totalExpected)}`}
									/>
									<Kpi
										label="Con rate scadute"
										value={overdueCount}
										tone={overdueCount ? "critical" : undefined}
									/>
								</>
							)}
							<Kpi
								label="Certificati scaduti"
								value={expiredDocsCount}
								tone={expiredDocsCount ? "critical" : undefined}
								note={
									expiringDocsCount
										? `${expiringDocsCount} in scadenza entro 30 giorni`
										: undefined
								}
							/>
						</KpiRow>

						{!feesAvailable && (
							<EmptyData>
								Quote e rate non sono ancora attive: si potranno inserire quando
								il server le salverà. Intanto qui trovi i documenti dei
								giocatori.
							</EmptyData>
						)}

						<VLFCard
							content={
								<DataTable
									columns={columns}
									rows={playersWithStatus.map((r) => ({
										...r,
										id: r.player.id,
									}))}
									onRowClick={(r) => navigate("/players/" + r.player.id)}
									empty="Nessun giocatore nella squadra."
								/>
							}
						/>
					</>
				)}
			</div>

			{openFeeModal && (
				<HandleFeeModal
					open={openFeeModal}
					close={() => setOpenFeeModal(false)}
					confirm={handleCreateFee}
					players={players}
				/>
			)}
			{openInstallmentModal && selectedInstallment && (
				<HandleInstallmentModal
					open={openInstallmentModal}
					close={() => {
						setOpenInstallmentModal(false);
						setSelectedInstallment(null);
					}}
					confirm={handlePayInstallment}
					installment={selectedInstallment}
				/>
			)}
		</Page>
	);
}

export default SecretariatPage;
